import { computed, defineComponent, ref, type Ref } from 'vue'
import { popToast, useToasts } from './hooks'
import type { Toast } from './types'

import DefaultIcon from 'assets/img/svg/icons/bell.svg?url'
import Icon from '../icon'
import { useElementBounding, useWindowSize } from '@vueuse/core'
import { useEffect } from '@/utils/reactive/useEffect'
import { type IBaseComponentProps } from '../base/types'
import { TOAST_MIN_HEIGHT, TOAST_MIN_WIDTH } from './constants'
import { defineVariants } from '@/utils/style/variant'

const useVariants = defineVariants({
  slots: {
    base: {
      class: 'flex items-center w-full max-w-xs p-4 mb-4 bg-white rounded-xs shadow-lg'
    },
    iconContainer: {
      class: 'inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-xs p-1'
    },
    title: {
      class: 'text-xs font-bold font-normal leading-[18px]'
    },
    subtitle: {
      class: 'text-gray-500 text-xs font-normal leading-[18px]'
    },
    closeButton: {
      class:
        'transition-all ms-auto -mx-1.5 -my-1.5 bg-transparent text-gray-400  rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 inline-flex items-center justify-center h-8 w-8',
      style: {
        pointerEvents: 'all'
      }
    }
  },
  variants: {
    hover: {
      true: {
        base: {},
        iconContainer: {},
        title: {},
        subtitle: {},
        closeButton: {
          style: {
            boxShadow: `inset 0 2px 4px 0 rgba(0, 0, 0, 0.1)`
          }
        }
      }
    },
    color: {
      success: {
        base: {
          class: 'bg-teal-50'
        },
        iconContainer: {
          class: 'text-emerald-600 bg-green-200',
          options: { preserveClasses: true }
        },
        title: {
          class: 'text-emerald-600'
        },
        subtitle: {
          class: 'text-gray-500'
        },
        closeButton: {}
      },
      warning: {
        base: {
          class: 'bg-orange-50'
        },
        iconContainer: {
          class: 'text-orange-500 bg-orange-100 dark:bg-orange-700 dark:text-orange-200',
          options: { preserveClasses: true }
        },
        title: {
          class: 'text-orange-600'
        },
        subtitle: {
          class: 'text-gray-500'
        },
        closeButton: {}
      },
      error: {
        base: {
          class: 'bg-[#fdf2f2]'
        },
        iconContainer: {
          class: 'text-red-500 bg-rose-200 dark:bg-red-800 dark:text-red-200',
          options: { preserveClasses: true }
        },
        title: {
          class: 'text-red-500'
        },
        subtitle: {
          class: 'text-gray-500'
        },
        closeButton: {}
      }
    }
  }
})

const ToastItem = defineComponent<
  IBaseComponentProps & {
    toast: Ref<Toast>
  }
>({
  setup(props) {
    const elRef = ref()
    const windowInfo = useWindowSize()
    const bounding = useElementBounding(elRef, { immediate: true })
    let initialized: boolean = false

    const { stop } = useEffect(() => {
      if (initialized) {
        stop()
        return
      }
      const toast = props.toast
      bounding.update()
      toast.value.update(props.toast)
      toast.value.height = Math.max(toast.value.height, bounding.height.value)
      toast.value.width = Math.max(toast.value.width, bounding.width.value)
      if (bounding.width.value > 10 && bounding.height.value > 10) {
        initialized = true
        stop()
      }

      //toast.value.bounds = bounds;
    }, [() => props.toast.value.animation.state.value])

    const opacity = computed(() => {
      const anim = props.toast.value.animation.state.value
      return `${anim * 100}%`
    })

    const variantColor = computed(() => props.toast.value.variant)
    const variants = useVariants({ color: variantColor })

    return () => {
      return (
        <div
          style={{
            position: 'fixed',
            right: '0px',
            marginLeft: 'auto',
            marginRight: 'auto',
            width: 'fit-content',
            height: 'fit-content',
            left: props.toast.value.position.x + 'px',
            top:
              windowInfo.height.value -
              (props.toast.value.position.y + bounding.height.value) +
              'px',
            opacity: opacity.value
          }}
          class="toast"
        >
          <div
            ref={elRef}
            class={variants.value.base.class}
            style={{
              minHeight: TOAST_MIN_HEIGHT + 'px',
              minWidth: TOAST_MIN_WIDTH + 'px'
            }}
            role="alert"
          >
            <div class={variants.value.iconContainer.class}>
              {props.toast.value.icon ? (
                <Icon src={props.toast.value.icon} />
              ) : (
                <Icon src={DefaultIcon} size={'1rem'} fill="currentColor" />
              )}
            </div>
            <div class="ms-3">
              <div class={variants.value.title.class}>{props.toast.value.title}</div>
              {props.toast.value.subtitle && (
                <div class={variants.value.subtitle.class}>{props.toast.value.subtitle}</div>
              )}
            </div>
            <button
              type="button"
              class={variants.value.closeButton.class}
              ref={variants.value.closeButton.ref}
              style={variants.value.closeButton.style}
              data-dismiss-target="#toast-success"
              aria-label="Close"
              onClick={() => popToast(props.toast.value.uid)}
            >
              <span class="sr-only">Close</span>
              <svg
                class="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
                style={{ pointerEvents: 'none' }}
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
            </button>
          </div>
        </div>
      )
    }
  }
})

ToastItem.props = ['toast']

const ToastContainer = defineComponent({
  setup() {
    const handler = useToasts()

    return () => {
      return (
        <div
          style={{
            width: '100vw',
            height: '100svh',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 520,
            pointerEvents: 'none'
          }}
        >
          {handler.state.toasts.map((toast, i) => {
            return <ToastItem toast={toast} key={`toast-${i}-${toast.value.uid}`} />
          })}
        </div>
      )
    }
  }
})

export default ToastContainer
